import { MEMORY_VOCABULARY_V1, memoryValuesConflict, } from "./memory-taxonomy.js";
// Memory retrieval ranking `relevance-v2` (docs/architecture/phase-5-memory.md, section 9.3, CHT-720). Pure and
// deterministic: no clock, no I/O, no randomness, no embeddings. The caller reads the eligible facts in SQL (section 9.2),
// gives them to `rankMemoryFacts` with the database clock, and gets the ordered result and the internal diagnostics.
//
// Every number below is PROVISIONAL (Phase 14 freezes launch values). A change to a table, a weight or a word list is a new
// ranking version (`MEMORY_RANKING_VERSION`), never a silent edit.
//
//   score = round(1000 * (0.50 K + 0.20 F + 0.15 T + 0.15 M)), an integer from 0 to 1000
//     K = how relevant the kind of fact is for the task (a table),
//     F = freshness, 0.5 ^ (ageDays / 365),
//     T = trust of the provenance,
//     M = 1 when a word of the request names the fact, else 0.
//   Confidence is NOT an input. It is metadata the fact carries (the trust contract: confidence is never permission).
//
// Order (Ruling R4): pinned facts first, then the score, then the last confirmation (newest first), then the fact id. The
// limit is HARD. Inside the pinned group a PERSONAL fact (an allergy, a religious diet) goes before a STANDARD one, so
// that when the pinned facts alone exceed the limit, the cut keeps the safety-relevant facts.
//
// Pinning (Ruling R13, which supersedes the first R13 of lane R): a PERSONAL dietary fact (an allergy, a religious diet) is
// pinned in EVERY task. A STANDARD dietary fact is pinned in DINING and ITINERARY only. The rule is about the sensitivity of
// the fact, not about the words of the request, because the tool `getCustomerContext` has no request text and a keyword
// table can miss a dining request.
//
// Versions: `relevance-v1` was the first revision. `relevance-v2` (the review fix round of CHT-720) changes three tables:
// the pinned rule above, the DINING keywords and the contradiction words of the current-request label.
export const MEMORY_RANKING_VERSION = "relevance-v2";
/** The task types of retrieval (design 9.1). */
export const memoryTaskContexts = Object.freeze([
    "DINING",
    "STAY",
    "EXPERIENCE",
    "ITINERARY",
    "GENERAL",
]);
/** The hard bounds of the retrieval `limit` (design 9.1). */
export const MEMORY_RETRIEVAL_LIMIT_MIN = 1;
export const MEMORY_RETRIEVAL_LIMIT_MAX = 20;
/** The keyword table of the task types (design 9.1, PROVISIONAL, English only). No keyword: GENERAL. */
export const MEMORY_TASK_KEYWORDS_V1 = Object.freeze({
    DINING: Object.freeze([
        "dinner",
        "lunch",
        "restaurant",
        "eat",
        "eating",
        "dine",
        "dining",
        "breakfast",
        "brunch",
        "food",
        "cafe",
        "bar",
        "meal",
        "tasting",
    ]),
    STAY: Object.freeze(["hotel", "villa", "room", "stay"]),
    EXPERIENCE: Object.freeze(["tour", "museum", "activity", "experience"]),
    ITINERARY: Object.freeze(["itinerary", "plan", "day", "trip"]),
});
/** The weights of the score. They sum to 1. PROVISIONAL. */
export const MEMORY_RANKING_WEIGHTS_V1 = Object.freeze({
    kind: 0.5,
    freshness: 0.2,
    trust: 0.15,
    match: 0.15,
});
/** Freshness halves every this many days (the half-life of `F`). PROVISIONAL. */
export const MEMORY_FRESHNESS_HALF_LIFE_DAYS = 365;
/**
 * The trust value `T` of a provenance label (design 9.3). `MODEL_INFERRED` is never expected here (only a CANDIDATE is a model
 * inference, and a candidate is never retrieved); it has a low value so that a surprise cannot rank above a stated fact.
 */
export const MEMORY_TRUST_VALUE_V1 = Object.freeze({
    CUSTOMER_CONFIRMED: 1.0,
    STAFF_CONFIRMED: 0.9,
    CUSTOMER_STATED_IN_CHAT: 0.75,
    MODEL_INFERRED: 0.5,
});
/** The kind relevance `K` by task (design 9.3, PROVISIONAL). 0 omits the fact, unless it is pinned. */
export const MEMORY_KIND_RELEVANCE_V1 = Object.freeze({
    DIETARY_PREFERENCE: { DINING: 1.0, STAY: 0.3, EXPERIENCE: 0.4, ITINERARY: 0.6, GENERAL: 0.6 },
    DINING_STYLE: { DINING: 1.0, STAY: 0.1, EXPERIENCE: 0.2, ITINERARY: 0.5, GENERAL: 0.5 },
    PROPERTY_PREFERENCE: { DINING: 0, STAY: 1.0, EXPERIENCE: 0, ITINERARY: 0.5, GENERAL: 0.4 },
    SCHEDULE_PREFERENCE: { DINING: 0.5, STAY: 0.4, EXPERIENCE: 0.7, ITINERARY: 1.0, GENERAL: 0.5 },
    INTEREST: { DINING: 0.3, STAY: 0.3, EXPERIENCE: 1.0, ITINERARY: 0.8, GENERAL: 0.5 },
    DISLIKED_EXPERIENCE: { DINING: 0.6, STAY: 0.4, EXPERIENCE: 1.0, ITINERARY: 0.8, GENERAL: 0.5 },
    TRAVEL_COMPANION_PATTERN: {
        DINING: 0.5,
        STAY: 0.8,
        EXPERIENCE: 0.8,
        ITINERARY: 0.8,
        GENERAL: 0.5,
    },
    EXPLICIT_INSTRUCTION: { DINING: 0.7, STAY: 0.7, EXPERIENCE: 0.7, ITINERARY: 0.8, GENERAL: 0.8 },
});
/**
 * The kinds that are pinned for a task (design 9.3 rule 2) whatever their sensitivity: always kept before the limit, because
 * the prompt treats a remembered dietary need as a hard filter. Dietary facts in DINING and ITINERARY. A PERSONAL dietary
 * fact is pinned in every task too (`MEMORY_PINNED_PERSONAL_KINDS_V2`).
 */
export const MEMORY_PINNED_KINDS_V1 = Object.freeze({
    DINING: Object.freeze(["DIETARY_PREFERENCE"]),
    STAY: Object.freeze([]),
    EXPERIENCE: Object.freeze([]),
    ITINERARY: Object.freeze(["DIETARY_PREFERENCE"]),
    GENERAL: Object.freeze([]),
});
/**
 * The kinds whose PERSONAL facts are pinned in EVERY task (Ruling R13). An allergy or a religious diet must reach the model
 * when it asks for a hotel, an experience or a plain question as well: a dietary need is a safety fact. PROVISIONAL.
 */
export const MEMORY_PINNED_PERSONAL_KINDS_V2 = Object.freeze([
    "DIETARY_PREFERENCE",
]);
/** Words of a code that never make a match: function words and negation. PROVISIONAL. */
const MATCH_STOP_WORDS = new Set([
    "no",
    "not",
    "with",
    "and",
    "or",
    "of",
    "the",
    "a",
    "an",
    "my",
    "me",
    "i",
    "to",
    "in",
    "on",
    "at",
    "for",
    "by",
    "before",
    "after",
    "from",
]);
// --- words -------------------------------------------------------------------------------------------------------
/** Lowercase words of a text, folded: accents removed, punctuation as a separator. */
function words(text) {
    return (text
        .normalize("NFKD")
        .replace(/\p{M}/gu, "")
        .toLowerCase()
        .match(/[a-z0-9]+/g) ?? []);
}
/**
 * A word without a plural `s` ("hotels" to "hotel") or without `ing` ("eating" to "eat", "planning" to "plan"). The `ing` is
 * cut only from a word of 6 letters or more, so at least 3 letters stay ("king" and "bring" are not cut), and a doubled final
 * consonant is folded to one (not `l`, `s`, `z`, so "calling" stays "call"). A word that ends in `ss` keeps it.
 */
const stem = (word) => {
    if (word.length >= 6 && word.endsWith("ing")) {
        const root = word.slice(0, -3);
        return /([bcdfghjkmnpqrtvwx])\1$/.test(root) ? root.slice(0, -1) : root;
    }
    return word.length > 3 && word.endsWith("s") && !word.endsWith("ss") ? word.slice(0, -1) : word;
};
const queryWords = (text) => {
    const list = words(text).map(stem);
    return { list, set: new Set(list) };
};
const stemSet = (text) => queryWords(text).set;
/**
 * The task type of a request text by the keyword table. The first task of the table (DINING, STAY, EXPERIENCE, ITINERARY)
 * whose keyword is a word of the text wins; no keyword, or no text, is GENERAL.
 */
export function deriveMemoryTask(queryText) {
    if (queryText === null)
        return "GENERAL";
    const query = stemSet(queryText);
    for (const task of ["DINING", "STAY", "EXPERIENCE", "ITINERARY"]) {
        if (MEMORY_TASK_KEYWORDS_V1[task].some((keyword) => query.has(stem(keyword))))
            return task;
    }
    return "GENERAL";
}
/**
 * `M`: a word of the request equals a word of the value (the code split at `_`, or the words of a free text), after folding and
 * plural folding. The words in `MATCH_STOP_WORDS` never match. The domain holds no English label text for a code (only an i18n
 * key), so the code words stand for the label words of the design.
 */
export function memoryMatchesQuery(value, queryText) {
    return matchesStems(value, queryText === null ? null : stemSet(queryText));
}
function matchesStems(value, query) {
    if (query === null || query.size === 0)
        return false;
    return words(value).some((word) => !MATCH_STOP_WORDS.has(word) && query.has(stem(word)));
}
// --- the score ---------------------------------------------------------------------------------------------------
/** `F`: 0.5 ^ (ageDays / half-life), from 0 to 1. A negative age (a clock that is behind) counts as 0. */
export function memoryFreshness(ageDays) {
    return 0.5 ** (Math.max(0, ageDays) / MEMORY_FRESHNESS_HALF_LIFE_DAYS);
}
export function memoryScore(terms) {
    const weights = MEMORY_RANKING_WEIGHTS_V1;
    return Math.round(1000 *
        (weights.kind * terms.k +
            weights.freshness * terms.freshness +
            weights.trust * terms.trust +
            weights.match * terms.match));
}
// --- the current request ---------------------------------------------------------------------------------------
/**
 * Words in a request that contradict a dietary code (PROVISIONAL, English only). A request that holds one of them, and does
 * not negate it, marks the durable preference as possibly in conflict for this turn. The words are folded and plural-folded
 * like every other word. A word with a second meaning (a country, a drink "spirit", a vegetable "burger") is left out.
 */
export const MEMORY_REQUEST_CONTRADICTION_TERMS_V1 = Object.freeze({
    vegetarian: [
        "fish",
        "seafood",
        "meat",
        "chicken",
        "beef",
        "pork",
        "lamb",
        "steak",
        "ham",
        "bacon",
        "salmon",
        "prawn",
        "shrimp",
        "sausage",
        "veal",
        "duck",
    ],
    vegan: [
        "fish",
        "seafood",
        "meat",
        "chicken",
        "beef",
        "pork",
        "lamb",
        "steak",
        "ham",
        "bacon",
        "salmon",
        "prawn",
        "shrimp",
        "sausage",
        "veal",
        "duck",
        "cheese",
        "dairy",
        "egg",
        "milk",
        "butter",
        "cream",
        "honey",
        "yogurt",
    ],
    pescatarian: [
        "meat",
        "chicken",
        "beef",
        "pork",
        "lamb",
        "steak",
        "ham",
        "bacon",
        "sausage",
        "veal",
        "duck",
    ],
    no_pork: ["pork", "ham", "bacon"],
    no_beef: ["beef", "steak", "veal"],
    no_alcohol: ["wine", "cocktail", "beer", "whisky", "champagne"],
    dairy_free: ["cheese", "cream", "milk", "butter", "yogurt", "dairy"],
    gluten_free: ["bread", "pasta", "pizza"],
    low_spice: ["spicy", "chilli"],
});
/** Words that negate the word after them: "no pork", "without meat", "avoid fish". Stemmed forms. PROVISIONAL. */
const NEGATORS = new Set(["no", "not", "without", "free", "avoid", "never"]);
/** Words that join a list: "no meat or fish" negates both. They do not count when a negator is searched. */
const LIST_JOINERS = new Set(["or", "and", "nor"]);
/** How many words before a term may hold its negator (list joiners not counted). */
const NEGATOR_REACH = 2;
/** Whether the word at `index` is negated: a negator within 2 words before it, or the word `free` right after it ("dairy free"). */
function isNegated(list, index) {
    if (list[index + 1] === "free")
        return true;
    let reach = 0;
    for (let at = index - 1; at >= 0 && reach < NEGATOR_REACH; at -= 1) {
        const word = list[at];
        if (LIST_JOINERS.has(word))
            continue;
        if (NEGATORS.has(word))
            return true;
        reach += 1;
    }
    return false;
}
/** Whether `word` (a folded word) occurs in the request and at least one occurrence is not negated. */
function namedAndNotNegated(query, word) {
    const target = stem(word);
    return query.list.some((candidate, index) => candidate === target && !isNegated(query.list, index));
}
/**
 * Whether the current request MAY conflict with a durable preference. Two ways: the request holds a contradicting word of the
 * table above, or it names (every word of) a code that conflicts with the fact (the conflict groups and the refinement pairs of
 * the taxonomy: "late dinner" against `early_dinner`). It labels the context line only: the customer should confirm, and the
 * fact is still returned and never changed. It does NOT label when
 *   - the request names the fact's own code words ("a dairy free restaurant" against `dairy_free`): the request agrees;
 *   - a negator stands within two words before the term, or `free` right after it ("no pork", "no meat or fish", "dairy-free");
 *   - the fact is PERSONAL (an allergy, a religious diet, an access need): a text heuristic must not demote it.
 */
export function memoryOverriddenByRequest(fact, queryText) {
    return overriddenByWords(fact, queryText === null ? null : queryWords(queryText));
}
function overriddenByWords(fact, query) {
    if (query === null || query.set.size === 0 || fact.sensitivity !== "STANDARD")
        return false;
    const own = words(fact.normalizedValue).filter((word) => !MATCH_STOP_WORDS.has(word));
    if (own.length > 0 && own.every((word) => query.set.has(stem(word))))
        return false;
    const terms = MEMORY_REQUEST_CONTRADICTION_TERMS_V1[fact.normalizedValue];
    if (fact.kind === "DIETARY_PREFERENCE" &&
        terms !== undefined &&
        terms.some((term) => namedAndNotNegated(query, term))) {
        return true;
    }
    return MEMORY_VOCABULARY_V1[fact.kind].some((entry) => {
        if (!memoryValuesConflict(fact, { kind: fact.kind, normalizedValue: entry.code }))
            return false;
        const named = words(entry.code).filter((word) => !MATCH_STOP_WORDS.has(word));
        return named.length > 0 && named.every((word) => namedAndNotNegated(query, word));
    });
}
export const memoryOmissionReasons = Object.freeze([
    "NOT_RELEVANT",
    "OVER_LIMIT",
    "PINNED_OVER_LIMIT",
    "REFUSED_SENSITIVITY",
]);
/**
 * The order of a ranked result: pinned first; inside the pinned group PERSONAL before STANDARD (safety first); then the score
 * descending; then the last confirmation newest first (never confirmed last); then the fact id ascending. A total order.
 */
export function compareRankedMemoryFacts(a, b) {
    if (a.pinned !== b.pinned)
        return a.pinned ? -1 : 1;
    if (a.pinned && a.sensitivity !== b.sensitivity)
        return a.sensitivity === "PERSONAL" ? -1 : 1;
    if (a.score !== b.score)
        return b.score - a.score;
    const aTime = a.lastConfirmedAt?.getTime() ?? null;
    const bTime = b.lastConfirmedAt?.getTime() ?? null;
    if (aTime !== bTime) {
        if (aTime === null)
            return 1;
        if (bTime === null)
            return -1;
        return bTime - aTime;
    }
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
const MS_PER_DAY = 86_400_000;
export function rankMemoryFacts(input) {
    const { task, now, limit } = input;
    if (!Number.isInteger(limit) ||
        limit < MEMORY_RETRIEVAL_LIMIT_MIN ||
        limit > MEMORY_RETRIEVAL_LIMIT_MAX) {
        throw new RangeError(`limit must be a whole number from ${MEMORY_RETRIEVAL_LIMIT_MIN} to ${MEMORY_RETRIEVAL_LIMIT_MAX}`);
    }
    const pinnedKinds = MEMORY_PINNED_KINDS_V1[task];
    // The words of the request, folded once for every fact.
    const query = input.queryText === null ? null : queryWords(input.queryText);
    const refused = [];
    const scored = [];
    const notRelevant = [];
    for (const fact of input.facts) {
        // Defence in depth: the SQL step never hands over a SENSITIVE fact, and neither does this one keep it.
        if (fact.sensitivity !== "STANDARD" && fact.sensitivity !== "PERSONAL") {
            refused.push(fact.id);
            continue;
        }
        const k = MEMORY_KIND_RELEVANCE_V1[fact.kind][task];
        const pinned = pinnedKinds.includes(fact.kind) ||
            (fact.sensitivity === "PERSONAL" && MEMORY_PINNED_PERSONAL_KINDS_V2.includes(fact.kind));
        const observed = Math.max(fact.firstObservedAt.getTime(), fact.lastConfirmedAt?.getTime() ?? Number.NEGATIVE_INFINITY, fact.lastEvidenceAt?.getTime() ?? Number.NEGATIVE_INFINITY);
        const ageDays = (now.getTime() - observed) / MS_PER_DAY;
        const ranked = {
            id: fact.id,
            kind: fact.kind,
            normalizedValue: fact.normalizedValue,
            sensitivity: fact.sensitivity,
            provenance: fact.provenance,
            confidencePermille: fact.confidencePermille,
            lastConfirmedAt: fact.lastConfirmedAt,
            lastObservedAt: new Date(observed),
            score: memoryScore({
                k,
                freshness: memoryFreshness(ageDays),
                trust: MEMORY_TRUST_VALUE_V1[fact.provenance],
                match: matchesStems(fact.normalizedValue, query?.set ?? null) ? 1 : 0,
            }),
            pinned,
            qualification: fact.contested ? "CONTESTED" : "NONE",
            overriddenByCurrentRequest: overriddenByWords(fact, query),
        };
        (k === 0 && !pinned ? notRelevant : scored).push(ranked);
    }
    scored.sort(compareRankedMemoryFacts);
    const items = scored.slice(0, limit);
    const cut = scored.slice(limit);
    notRelevant.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    refused.sort();
    const entry = (fact, decision, reason) => ({ factId: fact.id, decision, reason, score: fact.score, pinned: fact.pinned });
    const pinnedOverLimit = cut.filter((fact) => fact.pinned).length;
    const everyScored = [...scored, ...notRelevant];
    return {
        items,
        diagnostics: {
            rankingVersion: MEMORY_RANKING_VERSION,
            task,
            eligibleCount: everyScored.length,
            returnedCount: items.length,
            pinnedCount: everyScored.filter((fact) => fact.pinned).length,
            contestedCount: items.filter((fact) => fact.qualification === "CONTESTED").length,
            omitted: { NOT_RELEVANT: notRelevant.length, OVER_LIMIT: cut.length },
            pinnedOverLimit,
            refusedCount: refused.length,
            perFact: [
                ...items.map((fact) => entry(fact, "SELECTED", "SELECTED")),
                ...cut.map((fact) => entry(fact, "OMITTED", fact.pinned ? "PINNED_OVER_LIMIT" : "OVER_LIMIT")),
                ...notRelevant.map((fact) => entry(fact, "OMITTED", "NOT_RELEVANT")),
                ...refused.map((factId) => ({
                    factId,
                    decision: "OMITTED",
                    reason: "REFUSED_SENSITIVITY",
                    score: 0,
                    pinned: false,
                })),
            ],
        },
    };
}
